// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

/* eslint-disable prettier/prettier */
import {
  APP_INITIALIZER,
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
} from '@angular/core';
import versionAndBuild from '../version-and-build.json';

import { DatePipe } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { MsalGuard } from '@azure/msal-angular';
import {
  popperVariation,
  provideTippyConfig,
  provideTippyLoader,
  tooltipVariation,
} from '@ngneat/helipopper/config';
import { TranslateModule } from '@ngx-translate/core';
import { ParameterController } from './app/_controller/parameter.controller';
import { MockAPIService } from './app/api/mock-api/mocks.service';
import { AppRoutingModule } from './app/app-routing.module';
import { providers } from './app/auth-providers';
import { PatternMatchModule } from './app/modules/patternmatch/patternMatch.module';
import { AuthorizedComponent } from './app/modules/portal/components/authorized/authorized.component';
import { EmbeddedViewComponent } from './app/modules/portal/components/embeddedView/embeddedView.component';
import { InitComponent } from './app/modules/portal/components/init/init.component';
import { ApplicationInsightsService } from './app/modules/portal/services/application-insights/application-insights-service/application-insights.service';
import { ApplicationInsightsErrorHandler } from './app/modules/portal/services/application-insights/application-insights.error-service/application-insights-error-handler.service';
import { FormatService } from './app/modules/portal/services/format.service';
import { PortalWebAppConfigurationService } from './app/modules/portal/services/portal-web-app-configuration.service';
import { RouteService } from './app/modules/portal/services/routeService.service';
import { UserService } from './app/modules/portal/services/user.service';
import { SanitizeResourceUrlPipe } from './app/modules/shared/pipes/sanitizeResourceUrl.pipe';
import { MapApiService } from './app/modules/shared/services/map-api.service';
import { ScreenLayoutService } from './app/modules/shared/services/screen-layout-service/screen-layout.service';
import { SettingsService } from './app/modules/shared/services/settings.service';
import { SharedModule } from './app/modules/shared/shared.module';
import { TubeModule } from './app/modules/tube/tube.module';
import { environment } from './environments/environment';
import './shui-dependencies';
import { MockUserService } from './unit-test-helpers/users.service.mock';
import { CoolingCompanionModule } from './app/modules/cooling-companion/cooling-companion.module';

// inject version number
{
  const { build, version } = versionAndBuild;
  document.insertBefore(
    document.createComment(` version: ${version} `),
    document.querySelector('html'),
  );
  document.insertBefore(
    document.createComment(` build: ${build} `),
    document.querySelector('html'),
  );
}

if (environment.production) {
  enableProdMode();
}

// bootstrap app based on configs
{
  const authProviders = environment.msal
    ? [UserService, ...providers(environment.msal)]
    : [
        {
          provide: UserService,
          useClass: MockUserService,
        },
        {
          provide: MsalGuard,
          useValue: { canActivate: () => true },
        },
      ];

  const apiProviders = environment.mockApis
    ? [
        {
          provide: HTTP_INTERCEPTORS,
          useClass: MockAPIService,
          multi: true,
        },
      ]
    : [];

  bootstrapApplication(InitComponent, {
    providers: [
      provideTippyLoader(() => import('tippy.js')),
      provideTippyConfig({
        defaultVariation: 'tooltip',
        variations: {
          tooltip: tooltipVariation,
          popper: popperVariation,
        },
      }),
      importProvidersFrom(
        SharedModule,
        BrowserModule,
        FormsModule,
        AppRoutingModule,
        TranslateModule,
        TubeModule,
        PatternMatchModule,
        CoolingCompanionModule
      ),
      // use real auth providers?
      ...authProviders,
      // intercept an mock all api calls right in the app?
      ...apiProviders,
      MapApiService,
      RouteService,
      ScreenLayoutService,
      FormatService,
      SettingsService,
      ParameterController,
      PortalWebAppConfigurationService,
      AuthorizedComponent,
      TranslateModule,
      EmbeddedViewComponent,
      DatePipe,
      ApplicationInsightsService,
      {
        provide: APP_INITIALIZER,
        useFactory: (appInsights: ApplicationInsightsService) => {
          return () => {
            if (environment.applicationInsightsConnectionString) {
              console.log('Initializing Application Insights');
              appInsights.initializeApplicationInsights(
                environment.applicationInsightsConnectionString,
              );
            } else {
              console.log('Application Insights not configured');
            }
          };
        },
        deps: [ApplicationInsightsService],
        multi: true,
      },
      SanitizeResourceUrlPipe,
      { provide: ErrorHandler, useClass: ApplicationInsightsErrorHandler },
      provideHttpClient(withInterceptorsFromDi()),
      provideAnimations(),
    ],
  }).catch((err) => console.error(err));
}
